import { ApiErrorBody, type ApiErrorCode } from "@exam/shared";

/**
 * Typed fetch wrapper for the Worker API.
 *
 * Always sends cookies (`credentials: "include"`) because auth is a session
 * cookie, and always throws an ApiRequestError on non-2xx so callers can
 * `catch (e) { if (isApiError(e, "unauthorized")) ... }`.
 *
 * In dev, Vite proxies /api -> http://127.0.0.1:8787 (see vite.config.ts).
 * In production set VITE_API_BASE_URL if the API is on another origin.
 */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly issues?: Array<{ path: string; message: string }>;

  constructor(
    code: ApiErrorCode,
    status: number,
    message: string,
    issues?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
    this.issues = issues;
  }
}

export function isApiError(
  err: unknown,
  code?: ApiErrorCode,
): err is ApiRequestError {
  return (
    err instanceof ApiRequestError && (code === undefined || err.code === code)
  );
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** Serialized as JSON with the right Content-Type. */
  json?: unknown;
  /** Appended as a query string, skipping null/undefined. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { json, query, headers, ...rest } = options;

  let url = `${BASE_URL}${path}`;
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== null && value !== undefined) params.set(key, String(value));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: {
        ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
      ...rest,
    });
  } catch {
    throw new ApiRequestError("internal", 0, "Network request failed");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = undefined;
    }
  }

  if (!res.ok) {
    const parsed = ApiErrorBody.safeParse(payload);
    if (parsed.success) {
      throw new ApiRequestError(
        parsed.data.error.code,
        res.status,
        parsed.data.error.message,
        parsed.data.error.issues,
      );
    }
    throw new ApiRequestError("internal", res.status, `Request failed (${res.status})`);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>("GET", path, options),
  post: <T>(path: string, options?: RequestOptions) =>
    request<T>("POST", path, options),
  patch: <T>(path: string, options?: RequestOptions) =>
    request<T>("PATCH", path, options),
  put: <T>(path: string, options?: RequestOptions) =>
    request<T>("PUT", path, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>("DELETE", path, options),
};
